import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter, type Event } from '@patsnap/tahiti-core/common/event';
import psHttp from '@patsnap/common-ps-http';
import { I18nService } from '@analytics/shared/services/i18n.interface';
import { VueRouterService } from '@analytics/shared/services/vue-router-service.interface';
import {
    ASSISTANT_DOM_SELECTOR,
    CONTAINER_DOM_SELECTOR,
    HEADER_DOM_SELECTOR,
    ABST_DOM_SELECTOR,
    DESC_DOM_SELECTOR,
    CLMS_DOM_SELECTOR,
    SUPPORTED_PATHS,
} from '../constants';
import type { WujieProps, EntryState } from '../type/chemical-entry.type';

export const ChemicalEntryService = Symbol('ChemicalEntryService');

export interface ChemicalEntryService {
    state: EntryState;
    onStatesChange: Event<EntryState>;
    getWujieProps: () => WujieProps;
    updateButtonVisible: (visible: boolean) => void;
    updateHighlightEnabled: (enabled: boolean) => void;
}

@injectable()
export class ChemicalEntryServiceImpl implements ChemicalEntryService {
    private _onStatesChangeEmitter = new Emitter<EntryState>();
    readonly onStatesChange = this._onStatesChangeEmitter.event;

    constructor(
        @inject(I18nService)
        private readonly _i18n: I18nService,
        @inject(VueRouterService)
        private readonly _router: VueRouterService,
    ) {
    }

    private _state: EntryState = {
        buttonVisible: false,
        highlightEnabled: false,
    };

    public get state() {
        return this._state;
    }

    /**
     * 切换tab
     */
    private _switchTab(name: string) {
        const pathName = `/${name}`;

        if (!SUPPORTED_PATHS.includes(pathName)) {
            throw new Error(`Unsupported tab: ${name}`);
        }

        this._router.push({
            path: pathName,
            query: {
                ...this._router.currentRoute.query,
            },
        });
    }

    /**
     * 获取当前详情页的tab，因为存在双视图，所以是数组
     */
    private _getCurrentTab() {
        const path = this._router.currentRoute.path.replace(/(^\/|\/$)/g, '');
        return path.split('\'');
    }

    /**
     * 专利内容容器
     */
    private _getPatentContainerDom() {
        return document.querySelector(CONTAINER_DOM_SELECTOR);
    }

    /**
     * 被监听的放置结构式助手容器的父容器
     */
    private _getPatentHeaderDom() {
        return document.querySelector(HEADER_DOM_SELECTOR);
    }

    /**
     * 结构式助手按钮容器id
     */
    private _getChemicalBtnContainerDom() {
        return document.querySelector(`${HEADER_DOM_SELECTOR} ${ASSISTANT_DOM_SELECTOR}`);
    }

    /**
     * 获取摘要渲染容器的class
     */
    private _getAbstRenderClass() {
        return ABST_DOM_SELECTOR;
    }

    /**
     * 获取说明书渲染容器的class
     */
    private _getDescRenderClass() {
        return DESC_DOM_SELECTOR;
    }

    /**
     * 获取权利要求渲染容器的class
     */
    private _getClmsRenderClass() {
        return CLMS_DOM_SELECTOR;
    }

    public getWujieProps() {
        return {
            locale: this._i18n.locale,
            httpRequest: psHttp,
            switchTab: (name: string) => this._switchTab(name),
            getCurrentTab: () => this._getCurrentTab(),
            getPatentContainerDom: () => this._getPatentContainerDom(),
            getPatentHeaderDom: () => this._getPatentHeaderDom(),
            getChemicalBtnContainerDom: () => this._getChemicalBtnContainerDom(),
            getAbstRenderClass: () => this._getAbstRenderClass(),
            getDescRenderClass: () => this._getDescRenderClass(),
            getClmsRenderClass: () => this._getClmsRenderClass(),
        };
    }

    public updateButtonVisible(visible: boolean) {
        this._state.buttonVisible = visible;
        this._onStatesChangeEmitter.fire(this.state);
    }

    public updateHighlightEnabled(enabled: boolean) {
        this._state.highlightEnabled = enabled;
        this._onStatesChangeEmitter.fire(this.state);
    }
}
